import React, { useRef, useState } from 'react';
import { Button, ReadOnlyGuard, sparkAssetUrl, SparkIcon, Text, UploadArea } from '@spark';
import { ADD_DATA_MENU_ITEMS } from '@/features/datasets';

const STACK_STYLE = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--dimension-spacing-16)',
    alignItems: 'stretch',
};

const HEADER_STYLE = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--dimension-spacing-04)',
    alignItems: 'flex-start',
};

const DROPZONE_IDLE_BORDER = 'var(--dimension-line-width-subtle) dashed var(--semantic-border-default)';
const DROPZONE_DRAG_BORDER = 'var(--dimension-line-width-subtle) dashed var(--semantic-border-active, #008ef5)';

const DROPZONE_STYLE = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'var(--dimension-spacing-08)',
    padding: 'var(--dimension-spacing-16)',
    borderRadius: 'var(--dimension-border-radius-08)',
    background: 'var(--semantic-surface-muted)',
    width: '100%',
    boxSizing: 'border-box',
};

const COPY_STYLE = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--dimension-spacing-04)',
    alignItems: 'center',
    width: '100%',
    textAlign: 'center',
};

const ACTIONS_STYLE = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'var(--dimension-spacing-08)',
};

const BUTTON_ROW_STYLE = {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 'var(--dimension-spacing-08)',
};

const LINK_ROW_STYLE = {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 'var(--dimension-spacing-08)',
};

const TEXT_RESET_STYLE = {
    margin: 0,
};

const FILES_STYLE = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--dimension-spacing-08)',
    width: '100%',
};

const HIDDEN_INPUT_STYLE = {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
};

const DROPZONE_ACTIONS = [
    { id: 'upload', label: 'Upload Data', iconSlug: ADD_DATA_MENU_ITEMS.upload.iconSlug },
    { id: 'library', label: 'Choose From your Uploads', iconSlug: ADD_DATA_MENU_ITEMS.library.iconSlug },
];

const LINK_ACTIONS = [
    { id: 'huggingface', label: 'Hugging Face', iconSlug: ADD_DATA_MENU_ITEMS.huggingface.iconSlug },
    { id: 'generateAi', label: 'Generate With AI', iconSlug: ADD_DATA_MENU_ITEMS.generateAi.iconSlug },
];

export default function ImproveFlowAugmentationStep({ onOpenAddData, onFilesSelected, items = [] }) {
    const fileInputRef = useRef(null);
    const [isDragOver, setIsDragOver] = useState(false);

    const handleFiles = (fileList) => {
        if (!fileList || fileList.length === 0) return;
        onFilesSelected?.(Array.from(fileList));
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'copy';
        }
        setIsDragOver(true);
    };

    const handleDragLeave = (event) => {
        if (event.currentTarget.contains(event.relatedTarget)) return;
        setIsDragOver(false);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        setIsDragOver(false);
        handleFiles(event.dataTransfer?.files);
    };

    return (
        <div style={STACK_STYLE}>
            <div style={HEADER_STYLE}>
                <Text as="p" variant="lead-semibold" color="primary" style={TEXT_RESET_STYLE}>
                    Data Augmentation (Optional)
                </Text>
                <Text as="p" variant="base-regular" color="primary" style={TEXT_RESET_STYLE}>
                    Expand your dataset to improve coverage and model robustness across edge cases.
                </Text>
            </div>
            {items.length > 0 ? (
                <div style={FILES_STYLE} role="status" aria-label="Files added to this task">
                    {items.map((item) => (
                        <UploadArea
                            key={item.id}
                            state={item.state}
                            fileName={item.fileName}
                            fileSize={item.fileSize}
                            progress={item.progress}
                            showHelperText={Boolean(item.helperText)}
                            helperText={item.helperText}
                            fullWidth
                            multiple={false}
                        />
                    ))}
                </div>
            ) : null}
            <ReadOnlyGuard block>
                <div
                    style={{
                        ...DROPZONE_STYLE,
                        border: isDragOver ? DROPZONE_DRAG_BORDER : DROPZONE_IDLE_BORDER,
                    }}
                    data-drag-over={isDragOver ? 'true' : 'false'}
                    aria-label="Drop files to add more data"
                    onDragEnter={handleDragOver}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <img
                        src={sparkAssetUrl('illustrations/illustration-add-more-data.png')}
                        alt=""
                        width={90}
                        height={89}
                    />
                    <div style={COPY_STYLE}>
                        <Text as="p" variant="lead-semibold" color="primary" style={TEXT_RESET_STYLE}>
                            Add More Data
                        </Text>
                        <Text as="p" variant="base-regular" color="tertiary" style={TEXT_RESET_STYLE}>
                            CSV/JSON format supported
                        </Text>
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.json,text/csv,application/json"
                        multiple
                        style={HIDDEN_INPUT_STYLE}
                        onChange={(event) => {
                            handleFiles(event.target.files);
                            event.target.value = '';
                        }}
                    />
                    <div style={ACTIONS_STYLE}>
                        <div style={BUTTON_ROW_STYLE}>
                            {DROPZONE_ACTIONS.map((action) => (
                                <Button
                                    key={action.id}
                                    variant="tertiary"
                                    size="medium"
                                    showStartIcon
                                    startIcon={<SparkIcon slug={action.iconSlug} size="medium" />}
                                    onClick={() => {
                                        if (action.id === 'upload') {
                                            fileInputRef.current?.click();
                                            return;
                                        }
                                        onOpenAddData(action.id);
                                    }}
                                >
                                    {action.label}
                                </Button>
                            ))}
                        </div>
                        <div style={LINK_ROW_STYLE}>
                            {LINK_ACTIONS.map((action) => (
                                <Button
                                    key={action.id}
                                    variant="ghost"
                                    size="small"
                                    showStartIcon
                                    startIcon={<SparkIcon slug={action.iconSlug} size="small" />}
                                    onClick={() => onOpenAddData(action.id)}
                                >
                                    {action.label}
                                </Button>
                            ))}
                        </div>
                    </div>
                </div>
            </ReadOnlyGuard>
        </div>
    );
}
